<script setup lang="ts">
import BaseButton from '@/components/ui/BaseButton.vue';
import type { ReportDto } from '@/api/reports';
import { formatFileSize } from '@/stores/reports';

interface Props {
  reports: ReportDto[];
  downloading?: boolean;
}

interface Emits {
  (e: 'download', id: string): void;
}

withDefaults(defineProps<Props>(), { downloading: false });
const emit = defineEmits<Emits>();

function formatMonth(month: string): string {
  const [year, mm] = month.split('-');
  const date = new Date(Number(year), Number(mm) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
</script>

<template>
  <div class="overflow-x-auto" data-testid="report-list-table">
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-border text-left">
          <th class="py-3 pr-4 text-caption font-medium text-muted uppercase tracking-wide">Period</th>
          <th class="py-3 pr-4 text-caption font-medium text-muted uppercase tracking-wide">Generated</th>
          <th class="py-3 pr-4 text-caption font-medium text-muted uppercase tracking-wide text-right">Pages</th>
          <th class="py-3 pr-4 text-caption font-medium text-muted uppercase tracking-wide text-right">Size</th>
          <th class="py-3 text-caption font-medium text-muted uppercase tracking-wide text-right">Download</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="report in reports"
          :key="report.id"
          class="border-b border-border last:border-0 hover:bg-canvas-soft"
        >
          <td class="py-3 pr-4 font-medium text-fg">{{ formatMonth(report.month) }}</td>
          <td class="py-3 pr-4 text-muted font-mono tabular-nums">{{ formatDate(report.generatedAt) }}</td>
          <td class="py-3 pr-4 text-right font-mono tabular-nums text-muted">{{ report.pageCount }}</td>
          <td class="py-3 pr-4 text-right font-mono tabular-nums text-muted">{{ formatFileSize(report.fileSizeBytes) }}</td>
          <td class="py-3 text-right">
            <BaseButton
              variant="ghost"
              size="sm"
              :loading="downloading"
              :aria-label="`Download ${formatMonth(report.month)} report PDF`"
              @click="emit('download', report.id)"
            >
              Download PDF
            </BaseButton>
          </td>
        </tr>
        <tr v-if="reports.length === 0">
          <td colspan="5" class="py-8 text-center text-muted text-sm">
            No reports generated yet. Generate your first monthly report using the button above.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
